import { type AdditionalCost, Retention } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'

import IconChevronRight from '@/assets/icons/icon-chevron-right.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs'
import type { AccountingCostBody } from '@/modules/accounting/types.ts'
import { RETENTION_LABELS } from '@/modules/accounting/utils.ts'
import { classNames } from '@/utils/helpers.ts'
import { roundAmount } from '@/utils/number.ts'

export const defaultCostValue: AccountingCostBody = {
  retention: Retention.YEARLY,
  amount: 0,
  name: '',
  typeId: 0,
  valid: true,
}

export const getAdditionalCostsTableColumns = ({
  deleteCost,
  showCents,
}: {
  deleteCost: (id: number) => Promise<void>
  showCents: boolean
}) =>
  [
    {
      accessorKey: 'name',
      header: 'Name',
      sortingFn: 'auto',
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      sortingFn: 'auto',
      cell: ({ row }) => row.original.type?.name,
    },
    {
      accessorKey: 'cost',
      header: 'Costs',
      sortingFn: 'auto',
      cell: ({ row }) => {
        const cost = roundAmount(row.original.amount, showCents)

        return (
          <span>
            {cost} / {RETENTION_LABELS[row.original.retention!].toLowerCase()}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: <div className="w-full text-center">Action</div>,
      cell: function ActionsCell({ row }) {
        if (row.original.deletedAt) {
          return (
            <span className="flex w-full items-center justify-end gap-1 text-right italic text-red-500">
              Deleted{' '}
              <span className="text-text-sub/20 text-xs">
                ({format(new Date(row.original.deletedAt), 'MMM dd, yyyy')})
              </span>
            </span>
          )
        }

        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => {
                // @ts-ignore
                openDialog({
                  type: DIALOG_TYPE.ADDITIONAL_COST_DETAILS,
                  data: {
                    ...row.original,
                    typeId: row.original.type?.id ?? 0,
                  },
                })
              }}
            >
              Edit Costs <IconChevronRight />
            </Button>
            <button type="button">
              <RemoveIcon
                width={18}
                height={18}
                onClick={() => {
                  openDialog({
                    type: DIALOG_TYPE.ACCOUNTING_ADDITIONAL_COST_TYPE_DELETE,
                    data: row.original,
                    onSubmit: deleteCost,
                  })
                }}
                className={classNames(
                  'text-danger-default',
                  'hover:text-danger-dark',
                )}
              >
                Remove
              </RemoveIcon>
            </button>
          </div>
        )
      },
    },
  ] as ColumnDef<AdditionalCost>[]
